import { FC, useRef, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';

// Need to work with the PopperJS API to make the popper width the same as the anchor element's width
const popperModifiers = [
  {
    name: 'setPopperWidth',
    enabled: true,
    fn: function (data: any) {
      const elements = data?.instance?.state?.elements;
      if (!elements) return;
      const { popper, reference } = elements;
      popper.style.width = `${reference.offsetWidth}px`;
    },
    phase: 'beforeRead',
  },
];

interface DropdownOption {
  label: string;
}

interface DropdownProps {
  height?: string;
  options: DropdownOption[] | undefined;
  indexOfSelectedOption: number;
  onChange(selectedOptionIndex: number): void;
  onlyOneCatalog: boolean;
}

const Dropdown: FC<DropdownProps> = ({
  height,
  options,
  indexOfSelectedOption,
  onChange,
  onlyOneCatalog,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<any>(null);

  const handleSelection = (index: number) => {
    onChange(index);
    setOpen(false);
  };

  const handleToggle = () => !onlyOneCatalog && setOpen(!open);

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  return (
    <div>
      <ButtonGroup
        variant="contained"
        sx={height ? { height } : {}}
        ref={anchorRef}
        fullWidth
      >
        <Button
          sx={{ borderRadius: 0 }}
          disableRipple={onlyOneCatalog}
          disableTouchRipple={onlyOneCatalog}
          onClick={handleToggle}
        >
          {options && options[indexOfSelectedOption].label}
        </Button>
        {!onlyOneCatalog && (
          <Button
            sx={{ width: 'max-content', borderRadius: 0 }}
            size="small"
            onClick={handleToggle}
          >
            <ArrowDropDownIcon />
          </Button>
        )}
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        popperOptions={{ modifiers: popperModifiers }}
        sx={{ zIndex: 2 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'center top' }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClickAway}>
                <MenuList>
                  {options?.map((option, index) => (
                    <MenuItem
                      key={option.label}
                      disabled={index === 2}
                      selected={index === indexOfSelectedOption}
                      onClick={() => handleSelection(index)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default Dropdown;
